import { Component, OnInit, OnDestroy, AfterViewInit, ElementRef, ViewChild, NgZone } from '@angular/core';
import { SideMenuService } from 'src/app/shared/side-menu/side-menu.service';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { UsersService } from 'src/app/users/users.service';
import { DEVICES_TAB_GROUPS, SiteDashboardService } from './site-dashboard.service';
import * as _ from 'underscore';
import { filter, Subscription } from 'rxjs';
import { StudiesService } from 'src/app/study/studies.service';
import { CommonService } from 'src/app/shared/services/common.service';
import { SitesService } from 'src/app/sites/sites.service';
import { ChargingType } from 'src/app/home/site-dashboard/site-settings/site-utilization-settings/site-utilization-settings.interface';

@Component({
	selector: 'app-site-dashboard',
	templateUrl: './site-dashboard.component.html',
	styleUrls: ['./site-dashboard.component.css']
})
export class SiteDashboardComponent implements OnInit, OnDestroy, AfterViewInit {
	@ViewChild('cardContainer', { static: false }) cardContainer?: ElementRef;

	DEVICES_TAB_GROUPS = DEVICES_TAB_GROUPS;

	brmLink: (string | number)[] = [];
	openBrmInNewTab = false;
	doneScrolling: boolean = false;
	theUIInitDone: boolean = false;
	customerId = 0;
	siteId = 0;
	customer: any = {};
	site: any = {};
	enterprisePermissions: any = {};
	activeTab?: string;
	currentUser: any = {};

	dataSub: Subscription = new Subscription();
	routerEventsSub: Subscription = new Subscription();

	data = {};
	sitePages = [
		'site-info',
		'devices-listing',
		'site-notes',
		'site-warnings',
		'tags-management',
		'maintenance-report',
		'site-warranty',
		'working-days',
		'site-attachments',
		'site-studies',
		'site-utilization-iotah',
		'site-utilization-charglink',
		'site-utilization-battlink',
		'charglink-brm',
		'charging-fleet-reports',
		'charger-exception-reports',
		'truck-exception-reports',
		'battery-exception-reports',
		'site-trucks-reports',
		'site-batteries-reports',
		'battery-fleet-quick-view',
		'iotemeter-site-energy-usage',
		'iotemeter-site-exception-reports',
		'iotemeter-site-daily-reports',
		'site-panels',
	];

	// All CHARGlink BRM pages, incl. its inner tabs, map to the single 'charglink-brm' site tab.
	// Read off the router config so adding a BRM route needs no change here (ST-2397).
	get charglinkBrmPages(): string[] {
		return (this.route.routeConfig?.children ?? [])
			.filter(child => child.path === 'charglink-brm' || child.path?.startsWith('charglink-brm/'))
			.map(child => child.data?.pageName)
			.filter(Boolean);
	}

	site_has_iotah_devices: boolean = false;
	site_has_chargLink_devices: boolean = false;
	site_has_battLink_devices: boolean = false;
	site_has_brm_devices: boolean = false;
	site_has_iotemeter_devices: boolean = false;
	siteBatteryChargingType: ChargingType = ChargingType.CONVENTIONAL;
	hasStudies: boolean = false;
	showChargLinkBRMOptions: boolean = false;
	activeCharglinkBRMOption: string = '';

	siteHasDevices: boolean = false;
	siteHasStudies: boolean = false;
	isPSDevicesSite: boolean = false;

	constructor(
		public usersService: UsersService,
		public siteDashboardService: SiteDashboardService,
		public commonService: CommonService,
		private route: ActivatedRoute,
		private sideMenuService: SideMenuService,
		private router: Router,
		private studiesService: StudiesService,
		private sitesService: SitesService,
		private ngZone: NgZone,
		private hostRef: ElementRef,
	) { }

	ngOnInit() {
		this.currentUser = this.usersService.getCurrentUser();
		this.siteDashboardService.activePage = this.getSiteTab(this.route.snapshot.firstChild?.data?.pageName);
		this.siteId = parseInt(this.route.snapshot.params.siteId);

		if (!this.siteId || isNaN(this.siteId))
			return this.router.navigateByUrl('/404');

		this.dataSub = this.sideMenuService.data.subscribe((data:any) => {
			if (!data || !data.length)
				return;

			this.customerId = parseInt(this.route.snapshot.params.customerId);

			if (!_.isEqual(this.data, data)) {
				this.data = data;
				this.setGlobalSite(data);
				this.getBrmLink();
				this.getEnterprisePermission();
				this.redirectFromHiddenBatteryFleetQuickView();
			}
		});
		this.siteHasIotahOrChargeLinkDevices();
		this.getBrmLink();

		if (this.currentUser.has_access_to_any_study)
			this.checkHasStudies();

		for (const group in this.siteDashboardService.GROUP_TABS) {
			if (this.siteDashboardService.GROUP_TABS[group].includes(this.siteDashboardService.activePage)) {
				this.siteDashboardService.activeGroup = group;
				return;
			}
		}
		this.hideTabsInPerformancePage();
	}

	/*
		Vertical wheel over a tab strip scrolls it sideways, so the tabs off-screen can be reached
		without having to catch the scrollbar. Bound outside Angular because wheel fires often and
		every tick inside the zone would run change detection across the whole dashboard. At either
		end of the strip the event is left alone, so the page keeps scrolling as normal.
	*/
	scrollTabStripOnWheel = (event: WheelEvent) => {
		const strip = (event.target as HTMLElement)?.closest<HTMLElement>('nav.topnav ul.card-container');

		if (!strip || Math.abs(event.deltaY) <= Math.abs(event.deltaX))
			return;

		const maxScrollLeft = strip.scrollWidth - strip.clientWidth;
		const nextScrollLeft = Math.min(Math.max(strip.scrollLeft + event.deltaY, 0), maxScrollLeft);

		if (nextScrollLeft == strip.scrollLeft)
			return;

		strip.scrollLeft = nextScrollLeft;
		event.preventDefault();
	};

	ngAfterViewInit() {
		this.ngZone.runOutsideAngular(() => {
			this.hostRef.nativeElement.addEventListener('wheel', this.scrollTabStripOnWheel, { passive: false });
		});
	}

	ngAfterViewChecked() {
		if (!this.doneScrolling && this.theUIInitDone && this.cardContainer?.nativeElement?.querySelector('.active'))
			this.doneScrolling = this.commonService.scrollToActiveLink(this.cardContainer);
	}

	onActivate(activeComponent: any) {
		let tab = '';
		let componentName = activeComponent.constructor.name;

		switch(componentName) {
			case 'SiteInfoComponent':
				tab = 'site-info';
				break;
			case 'DevicesListingComponent':
				tab = 'devices-listing';
				break;
			case 'DeviceDashboardComponent':
				tab = 'analytics-settings';
				break;
			case 'SiteNotesComponent':
				tab = 'site-notes';
				break;
			case 'TagsManagementComponent':
				tab = 'tags-management';
				break;
			case 'MaintenanceReportComponent':
				tab = 'maintenance-report';
				break;
			case 'SiteWarrantyComponent':
				tab = 'site-warranty';
				break;
			case 'SiteSettingsPage':
				tab = 'site-settings-page';
				break;
			case 'SiteAttachmentsComponent':
				tab = 'site-attachments';
				break;
			case 'SiteStudiesComponent':
				tab = 'site-studies';
				break;
			case 'CharglinkBrmComponent':
				tab = 'charglink-brm';
				break;
			case 'SiteTruckReportsComponent':
				tab = 'site-trucks-reports';
				break;
			case 'IotemeterSiteEnergyUsageComponent':
				tab = 'iotemeter-site-energy-usage';
				break;
			case 'IotemeterSiteExceptionReportsComponent':
				tab = 'iotemeter-site-exception-reports';
				break;
			case 'IotemeterSiteDailyReportsComponent':
				tab = 'iotemeter-site-daily-reports';
				break;
			case 'SitePanelsComponent':
				tab = 'site-panels';
				break;
			default:
				break;
		}

		this.activeTab = tab;
	}

	canShowGroup(group: string): boolean {
		let show = false;

		switch (group) {
			case DEVICES_TAB_GROUPS.TRUCK_REPORTS:
				show = this.site_has_iotah_devices && !this.isPSDevicesSite;
				break;
			case DEVICES_TAB_GROUPS.CHARGER_FLEET_REPORTS:
				show = this.site_has_chargLink_devices || this.site_has_brm_devices;
				break;
			case DEVICES_TAB_GROUPS.BATTERIES_REPORTS:
				show = this.site_has_battLink_devices;
				break;
			case DEVICES_TAB_GROUPS.IOTEMETER_REPORTS:
				show = this.site_has_iotemeter_devices;
				break;
			default:
				break;
		}

		return show;
	}

	// ST-2679: shown on sites with a Lithium BATTlink, or whose Charging Type is Opportunity/Fast Charging
	canShowBatteryFleetQuickView(): boolean {
		return this.site_has_battLink_devices && (+this.site?.lithium_battlink_devices_count > 0 || this.siteBatteryChargingType == ChargingType.OPPORTUNITY_FAST);
	}

	// ST-2679 AC-5: the page opened by URL on a site it is hidden for lands on the first Batteries Reports tab.
	// Needs both the site (side menu data) and the device counts, which arrive in either order.
	redirectFromHiddenBatteryFleetQuickView() {
		if (this.siteDashboardService.activePage != 'battery-fleet-quick-view' || !this.theUIInitDone || !this.site?.id)
			return;

		if (this.canShowBatteryFleetQuickView())
			return;

		const firstTab = this.siteDashboardService.GROUP_TABS[DEVICES_TAB_GROUPS.BATTERIES_REPORTS][0];
		this.siteDashboardService.activeGroup = DEVICES_TAB_GROUPS.BATTERIES_REPORTS;
		this.siteDashboardService.setActiveTab(firstTab);
		this.router.navigate(['/', this.customerId, this.siteId, firstTab], { replaceUrl: true });
	}

	isGroupSelected(group: string): boolean {
		for (const groupItem in this.siteDashboardService.GROUP_TABS) {
			if (groupItem == group && this.siteDashboardService.GROUP_TABS[group].includes(this.siteDashboardService.activePage))
				return true;
		}

		return false;
	}

	selectGroup(group: string) {
		this.siteDashboardService.activeGroup = group;
		this.siteDashboardService.setActiveTab(this.siteDashboardService.GROUP_TABS[group][0]);
		this.router.navigate(['/', this.customerId, this.siteId, this.siteDashboardService.activePage]);
	}

	getEnterprisePermission() {
		this.usersService.getPermissions(this.siteId).subscribe((permissions: any) => {
			this.enterprisePermissions = permissions;
			this.siteDashboardService.setPermissionsOfCurrentSite(permissions);
		});
	}

	setGlobalSite(data: any) {
		const customers = _.indexBy(data, "id");
		this.customer = customers[this.customerId];

		if (!this.customer)
			return this.redirectToRightCustomerId(data);

		this.sideMenuService.setCurrentCustomer(customers[this.customerId]);
		let sites: any = {};
		if(this.customer) {
			sites = _.indexBy(this.customer.sites, "id");
			this.site = sites[this.siteId];
			this.isPSDevicesSite = this.site?.name == this.commonService.powerStudiesDevicesSiteName;
			if (!this.site)
				return this.redirectToRightCustomerId(data);
		}
		this.sideMenuService.setCurrentSite(sites[this.siteId] || {});
	}

	redirectToRightCustomerId(data: any) {
		let customerId, siteId, child, queryParams;

		for (const customer of data) {
			for (const site of customer.sites) {
				if (site.id == this.siteId) {
					customerId = customer.id;
					siteId = site.id;
					child = this.route.firstChild?.routeConfig?.path;
					if (child) {
						queryParams = this.route.firstChild?.snapshot?.queryParams;
						const deviceId = this.route.firstChild?.snapshot?.params?.deviceId;
						if (deviceId)
							child = child.replace(":deviceId", deviceId);
					}
					break;
				}
			}
		}
		if (!customerId || !siteId)
			return;

		if (!child)
			return this.router.navigate(['/', customerId , siteId]);

		let qp = '';
		if (queryParams && Object.keys(queryParams).length > 0) {
			qp += '?';
			for (let p in queryParams) {
				qp = qp + p + '='+ queryParams[p];
			}
		}
		return this.router.navigateByUrl('/'+ customerId + '/' + this.siteId + '/'  + child + qp);
	}

	ngOnDestroy() {
		this.dataSub.unsubscribe();
		this.routerEventsSub.unsubscribe();
		this.hostRef.nativeElement.removeEventListener('wheel', this.scrollTabStripOnWheel);
	}

	siteHasIotahOrChargeLinkDevices() {
		this.siteDashboardService.siteHasIotahOrChargeLinkDevices(this.siteId).subscribe((res: any) => {
			this.site_has_iotah_devices = !!res.iotah_devices;
			this.site_has_chargLink_devices = !!res.chargLink_devices;
			this.site_has_battLink_devices = !!res.battLink_devices;
			this.site_has_brm_devices = !!res.brm_devices;
			this.site_has_iotemeter_devices = !!res.iotemeter_devices;
			this.siteBatteryChargingType = res.charging_type ?? ChargingType.CONVENTIONAL;

			this.siteHasDevices = this.site_has_iotah_devices ||
				this.site_has_chargLink_devices ||
				this.site_has_battLink_devices ||
				this.site_has_brm_devices ||
				this.site_has_iotemeter_devices;

			this.theUIInitDone = true;
			this.redirectFromHiddenBatteryFleetQuickView();
		});
	}

	checkHasStudies() {
		this.studiesService.checkHasStudies(this.siteId).subscribe((res: any) => {
			this.hasStudies = res;
			this.siteHasStudies = this.siteHasStudies || this.hasStudies;
		});
	}

	openChargLinkBRMOptions() {
		this.showChargLinkBRMOptions = true;
	}

	updateChargLinkBRMOption(option: string) {
		this.activeCharglinkBRMOption = option;
		this.showChargLinkBRMOptions = false;
	}

	enableBuzzer() {
		this.sitesService.enableBuzzer();
	}

	onBrmTabClick() {
		if (!this.openBrmInNewTab)
			this.siteDashboardService.setActiveTab('charglink-brm');

		this.enableBuzzer();
	}

	getBrmLink() {
		this.brmLink = ['/', 'charglink-brm', 'public', this.site?.brm_public_token || 0];
		this.openBrmInNewTab = true;

		if (this.usersService.hasAccessFunction('show_charglink_brm')) {
			this.brmLink = ['/', this.customerId, this.siteId, 'charglink-brm'];
			this.openBrmInNewTab = false;
		}
	}

	getSiteTab(pageName: string): string {
		if (this.charglinkBrmPages.includes(pageName))
			return 'charglink-brm';

		return this.sitePages.includes(pageName) ? pageName : 'device-dash';
	}

	hideTabsInPerformancePage() {
		this.routerEventsSub = this.router.events
			.pipe(filter(event => event instanceof NavigationEnd))
			.subscribe((event: NavigationEnd) => {
			const url = event.urlAfterRedirects.split(/[?#]/)[0];
			const segments = url.split('/').filter(Boolean);
			let tab = segments[segments.length - 1];

			if (segments.includes('performance')) {
				tab = 'device-dash';
			}

			if (segments.includes('charglink-brm')) {
				tab = 'charglink-brm';
			}

			this.siteDashboardService.setActiveTab(tab);
		});
	}
}
